import { Button, Card, CardTitle } from '@tark/shared/components';
import type { ScheduleCoachDashboardAttentionItem } from '@tark/shared/types';
import type { CoachOpsRailProps } from './coachStudioTypes';
import { attentionIcon, formatCodeLabel, formatRelativeTime, severityClasses } from './coachStudioUtils';
import { CreditCard, ReceiptText } from 'lucide-react';

export default function CoachOpsRail({
  attentionItems,
  bookingRequests,
  money,
  onNavigate,
  onOpenWork,
  onOpenCatalog,
}: CoachOpsRailProps) {
  const hasMoneySignal = Boolean(
    (money?.amountDue ?? 0) > 0
      || (money?.paidAmount ?? 0) > 0
      || (money?.balanceAmount ?? 0) > 0
      || (money?.refundedAmount ?? 0) > 0
  );
  const title = attentionItems.length > 0 ? 'Needs attention' : bookingRequests.length > 0 ? 'Requests' : 'Money';

  return (
    <Card variant="default" padding="none" className="overflow-hidden">
      <div className="border-b border-border px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle>{title}</CardTitle>
          {attentionItems.length > 0 ? (
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
              {attentionItems.length} open
            </span>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 p-5">
        {hasMoneySignal ? <MoneySnapshot money={money} onOpenCatalog={onOpenCatalog} /> : null}

        {attentionItems.slice(0, 3).length > 0 ? (
          <div className="space-y-2">
            {attentionItems.slice(0, 3).map((item) => (
              <AttentionItemRow key={item.key} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        ) : null}

        {bookingRequests.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Requests</p>
            {bookingRequests.slice(0, 3).map((request) => (
              <button
                key={request.bookingId}
                type="button"
                onClick={onOpenWork}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/20 p-3 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-text">{request.athleteDisplayName}</p>
                  <p className="mt-0.5 truncate text-xs text-text-muted">
                    {[request.programName, formatRelativeTime(request.requestedAt)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className="rounded-full border border-primary/15 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                  {formatCodeLabel(request.bookingStatus)}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function MoneySnapshot({
  money,
  onOpenCatalog,
}: {
  money: CoachOpsRailProps['money'];
  onOpenCatalog: () => void;
}) {
  if (!money) {
    return null;
  }

  const balance = money.balanceAmount ?? 0;
  const paid = money.paidAmount ?? 0;
  const currency = money.currencyCode || 'USD';

  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Money</p>
          </div>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{formatMoney(balance, currency)}</p>
          <p className="text-xs text-text-muted">{money.dueCount || 0} due</p>
        </div>
        <div className="rounded-2xl bg-primary/10 p-3 text-primary">
          <ReceiptText className="h-5 w-5" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <MiniMoney label="Paid" value={formatMoney(paid, currency)} />
        <MiniMoney label="Partial" value={String(money.partialCount || 0)} />
      </div>

      <Button variant="outline" className="mt-4 w-full" onClick={onOpenCatalog}>
        Open Orders
      </Button>
    </div>
  );
}

function MiniMoney({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-background/70 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-text">{value}</p>
    </div>
  );
}

function formatMoney(value?: number | null, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value ?? 0);
}

function AttentionItemRow({
  item,
  onNavigate,
}: {
  item: ScheduleCoachDashboardAttentionItem;
  onNavigate: (actionKey: string) => void;
}) {
  const Icon = attentionIcon(item);

  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-3 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-semibold text-text">{item.title}</p>
              <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${severityClasses(item.severity)}`}>
                {item.severity}
              </span>
            </div>
          </div>
        </div>

        <Button variant="outline" onClick={() => onNavigate(item.actionKey)}>
          {item.actionLabel}
        </Button>
      </div>
    </div>
  );
}
